import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import BrandMark from '../components/BrandMark'
import { useAuth } from '../auth/AuthProvider'

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/places', label: 'Places' },
  { to: '/people', label: 'People' },
  { to: '/chat', label: 'Talk to History' },
  { to: '/news', label: 'History News', secondary: true },
  { to: '/platform-overview', label: 'Platform Overview' },
  { to: '/about', label: 'About', secondary: true },
]

const userMenuLinks = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/settings', label: 'Settings' },
]

const AppLayout = ({ children }) => {
  const currentYear = new Date().getFullYear()
  const { isAuthenticated, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const buttonRef = useRef(null)
  const menuRef = useRef(null)
  const [menuOpen, setMenuOpen] = useState(false)

  const closeMenu = () => setMenuOpen(false)

  const toggleMenu = () => setMenuOpen((prev) => !prev)

  const handleMenuLinkClick = (to) => {
    navigate(to)
    closeMenu()
  }

  const handleLogout = () => {
    logout()
    closeMenu()
  }

  useEffect(() => {
    if (!menuOpen) return undefined

    const handlePointerDown = (event) => {
      const buttonEl = buttonRef.current
      const menuEl = menuRef.current
      const path = typeof event.composedPath === 'function' ? event.composedPath() : []
      const targetNode = event.target
      const insideButton = buttonEl
        ? path.includes(buttonEl) || (targetNode instanceof Node && buttonEl.contains(targetNode))
        : false
      const insideMenu = menuEl
        ? path.includes(menuEl) || (targetNode instanceof Node && menuEl.contains(targetNode))
        : false

      if (!insideButton && !insideMenu) {
        closeMenu()
      }
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu()
      }
    }

    document.addEventListener('pointerdown', handlePointerDown, true)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [menuOpen])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    closeMenu()
  }, [location.pathname])

  return (
    <div className="app-shell">
      <header className="site-header">
        <div className="content-container header-inner">
          <NavLink to="/" className="brand" aria-label="Places in Time home">
            <BrandMark
              width={48}
              height={48}
              aria-hidden="true"
              className="brand-mark"
            />
            <span className="brand-text">
              <span className="brand-line">Places</span>
              <span className="brand-line">in Time</span>
            </span>
          </NavLink>
          <div className="header-nav-cluster">
            <nav className="site-nav">
              {navLinks.map((link) => {
                if (link.href) {
                  return (
                    <a
                      key={link.href}
                      className={['nav-link', link.secondary ? 'nav-link--secondary' : '']
                        .filter(Boolean)
                        .join(' ')}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link.label}
                    </a>
                  )
                }

                return (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    className={({ isActive }) =>
                      ['nav-link', link.secondary ? 'nav-link--secondary' : '', isActive ? 'is-active' : '']
                        .filter(Boolean)
                        .join(' ')
                    }
                  >
                    {link.label}
                  </NavLink>
                )
              })}
            </nav>
            <div className="user-menu" data-open={menuOpen ? 'true' : undefined}>
              <button
                type="button"
                className="user-menu-button"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                ref={buttonRef}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation()
                  toggleMenu()
                }}
              >
                <span className="user-menu-label">Account</span>
                <span className="user-menu-caret" aria-hidden="true">?</span>
              </button>
              {menuOpen && (
                <div className="user-menu-panel" role="menu" ref={menuRef}>
                  {isAuthenticated ? (
                    <>
                      {userMenuLinks.map((link) => (
                        <button
                          key={link.to}
                          type="button"
                          className="user-menu-link"
                          onClick={() => handleMenuLinkClick(link.to)}
                          role="menuitem"
                        >
                          {link.label}
                        </button>
                      ))}
                      <button type="button" className="user-menu-link" onClick={handleLogout} role="menuitem">
                        Log out
                      </button>
                      <p className="user-menu-note">Signed-in users can update chat preferences here.</p>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="user-menu-link"
                        onClick={() => handleMenuLinkClick('/login')}
                        role="menuitem"
                      >
                        Log in
                      </button>
                      <button
                        type="button"
                        className="user-menu-link"
                        onClick={() => handleMenuLinkClick('/register')}
                        role="menuitem"
                      >
                        Register
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      <main className="site-main">
        <div className="content-container">{children}</div>
      </main>
      <footer className="site-footer">
        <div className="content-container">
          <div className="footer-inner">
            <div className="footer-column footer-brand-column">
              <div className="footer-brand">
                <BrandMark
                  width={56}
                  height={56}
                  aria-hidden="true"
                  className="brand-mark"
                />
                <div>
                  <p className="eyebrow">Places in Time</p>
                  <p className="footer-tagline">WHERE HISTORY ANSWERS BACK</p>
                </div>
              </div>
            </div>
            <div className="footer-column footer-links">
              <p className="eyebrow">Explore</p>
              <p>
                <NavLink to="/about" className="nav-link--secondary">About Places in Time</NavLink>
              </p>
              <p>
                <NavLink to="/places">Places atlas</NavLink>
              </p>
              <p>
                <NavLink to="/people">Figures roster</NavLink>
              </p>
              <p>
                <NavLink to="/news" className="nav-link--secondary">History News</NavLink>
              </p>
            </div>
            <div className="footer-column footer-legal">
              <p className="eyebrow">Legal</p>
              <p>
                <NavLink to="/privacy-policy-gdpr">Privacy & GDPR</NavLink>
              </p>
              <p>
                <NavLink to="/terms-of-use">Terms of Use</NavLink>
              </p>
              <p>
                <NavLink to="/impressum">Impressum</NavLink>
              </p>
            </div>
            <div className="footer-column footer-social">
              <p className="eyebrow">Follow</p>
              <div className="social-links">
                <a
                  href="https://www.facebook.com/placesintimedotcom"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Places in Time on Facebook"
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    role="img"
                    aria-hidden="true"
                  >
                    <path
                      fill="currentColor"
                      d="M15 8h3V4h-3c-2.8 0-5 2.2-5 5v2H7v4h3v7h4v-7h3l1-4h-4V9c0-.6.4-1 1-1z"
                    />
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/infoplacesintime/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Places in Time on Instagram"
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    role="img"
                    aria-hidden="true"
                  >
                    <path
                      fill="currentColor"
                      d="M7 3C4.2 3 2 5.2 2 8v8c0 2.8 2.2 5 5 5h8c2.8 0 5-2.2 5-5V8c0-2.8-2.2-5-5-5H7zm0 2h8c1.7 0 3 1.3 3 3v8c0 1.7-1.3 3-3 3H7c-1.7 0-3-1.3-3-3V8c0-1.7 1.3-3 3-3zm9 1a1 1 0 100 2 1 1 0 000-2zM11 7a5 5 0 100 10 5 5 0 000-10zm0 2a3 3 0 110 6 3 3 0 010-6z"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <small>&copy; {currentYear} Places in Time. All rights reserved.</small>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default AppLayout
